import React, { useState } from "react";
import { CHART_1, CHART_2, INK, MUTED, GRID, PAPER } from "./palette";

// ---- added_variable: the added-variable (partial regression) plot for one predictor ----
// Props from ChartData#added_variable: points [[x residual, y residual]], slope (least squares through
// them), coefficient (the full model's b for the focal predictor, from the recompute), simpleSlope
// (the predictor alone), focalName, outcomeName, otherNames, xUnit/yUnit (one unit, e.g. "hour").
// Tokens, axes and tooltips follow ConceptChart.jsx; green is the data, teal the slope being taught.
const W = 640, TOP = 16, RIGHT = 16;

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
const minus = (t) => (t < 0 ? `−${-t}` : t);
const scale = (ax, a, b) => (v) => a + ((v - ax.min) / (ax.max - ax.min)) * (b - a);
const lowerWords = (s) => s.split(" ").map((w) => (/^[A-Z][a-z]+$/.test(w) ? w.toLowerCase() : w)).join(" ");
const sentenceList = (parts) => (parts.length < 2 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`);
const article = (w) => `${/^(hour|honest|[aeiou])/i.test(w) ? "an" : "a"} ${w}`;
const units = (unit, v = 2) => (unit ? ` ${v === 1 ? unit : `${unit}s`}` : "");

// Nice bounds and ticks around values that run either side of zero (as ConceptChart's axis()).
function axis(xs, pad = 0.05) {
	const lo = Math.min(...xs), hi = Math.max(...xs), span = hi - lo || 1;
	const rough = span / 6, mag = 10 ** Math.floor(Math.log10(rough));
	const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= rough);
	const min = Math.floor((lo - span * pad) / step) * step, max = Math.ceil((hi + span * pad) / step) * step;
	const ticks = [];
	for (let t = min; t <= max + step / 2; t += step) ticks.push(+t.toFixed(10));
	return { min, max, ticks };
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, 16), W - RIGHT - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${TOP})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

export default function AddedVariableChart({ points, slope, coefficient, simpleSlope, focalName, outcomeName, otherNames = [], xUnit, yUnit, noun = "participant" }) {
	const [hover, setHover] = useState(null);
	const height = 320, box = { l: 44, r: W - 132, t: TOP + 18, b: height - 40 };
	const xs = points.map((p) => p[0]), ys = points.map((p) => p[1]);
	const axX = axis(xs);
	const axY = axis([...ys, slope * axX.min, slope * axX.max]);
	const x = scale(axX, box.l, box.r), y = scale(axY, box.b, box.t);
	const focal = lowerWords(focalName), outcome = lowerWords(outcomeName), others = sentenceList(otherNames.map(lowerWords));
	const b = fmt(coefficient);
	const away = (v, unit) => `${fmt(Math.abs(v))}${units(unit, Math.abs(v))} ${v < 0 ? "below" : "above"} what ${others} predict`;
	const lineTip = { cx: x(axX.max), lines: [`Best-fitting line through the leftovers`, `Slope ${fmt(slope, 3)}${yUnit ? ` ${yUnit}s` : ""} per${xUnit ? ` ${xUnit}` : " 1"} of ${focal}`, `${focalName}'s b in the full model: ${fmt(coefficient, 3)}`] };
	return (
		<figure>
			<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
				<span className="flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: CHART_2 }} />One {noun}</span>
				<span className="flex items-center gap-1.5"><span className="inline-block h-0.5 w-4 rounded" style={{ background: CHART_1 }} />Best-fitting line: its slope is {focal}'s coefficient, {b}</span>
			</div>
			<svg viewBox={`0 0 ${W} ${height}`} className="mt-2 w-full" role="img"
				aria-label={`Added-variable plot for ${focal}: ${points.length} ${noun}s' ${outcome} left over after ${others}, against their ${focal} left over after the same. The best-fitting line has slope ${b}, which is ${focal}'s partial regression coefficient in the full model. On its own, ${focal}'s slope is ${fmt(simpleSlope)}.`}>
				{axY.ticks.map((t) => (
					<g key={`y${t}`}>
						<line x1={box.l} x2={box.r} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={box.l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{minus(t)}</text>
					</g>
				))}
				{axX.ticks.map((t) => <text key={`x${t}`} x={x(t)} y={box.b + 16} fontSize="11" fill={MUTED} textAnchor="middle">{minus(t)}</text>)}
				<text x={box.r} y={box.b + 32} fontSize="11" fill={MUTED} textAnchor="end">{focalName} left over after {others}{xUnit ? ` (${xUnit}s)` : ""}</text>
				<text x={box.l} y={box.t - 8} fontSize="11" fill={MUTED}>{outcomeName} left over after {others}{yUnit ? ` (${yUnit}s)` : ""}</text>
				<line x1={x(0)} x2={x(0)} y1={box.t} y2={box.b} stroke={MUTED} strokeWidth="1" strokeDasharray="4 3" opacity="0.6" />
				{points.map(([a, e], i) => (
					<circle key={i} cx={x(a)} cy={y(e)} r="4" fill={CHART_2} stroke={PAPER} strokeWidth="2" opacity="0.85"
						onMouseEnter={() => setHover({ cx: x(a), lines: [`One ${noun}`, `${focalName}: ${away(a, xUnit)}`, `${outcomeName}: ${away(e, yUnit)}`] })} onMouseLeave={() => setHover(null)} />
				))}
				<g onMouseEnter={() => setHover(lineTip)} onMouseLeave={() => setHover(null)}>
					<line x1={x(axX.min)} x2={x(axX.max)} y1={y(slope * axX.min)} y2={y(slope * axX.max)} stroke="transparent" strokeWidth="10" />
					<line x1={x(axX.min)} x2={x(axX.max)} y1={y(slope * axX.min)} y2={y(slope * axX.max)} stroke={CHART_1} strokeWidth="2" />
				</g>
				<text x={box.r + 10} y={y(slope * axX.max) - 2} fontSize="12" fill={INK} fontWeight="600">Slope {b}</text>
				<text x={box.r + 10} y={y(slope * axX.max) + 12} fontSize="11" fill={MUTED}>= {focal}'s b</text>
				{hover && <Tooltip x={hover.cx} lines={hover.lines} />}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each dot is one {noun}. Across: how far their {focal} is from what their {others} predict. Up: the same for {outcome}.
				The slope of the line through these leftovers is {b}, exactly {focal}'s partial regression coefficient in the full model:
				among {noun}s with the same {others}, those {xUnit ? article(xUnit) : "1 unit"} apart in {focal} differ by {b}{units(yUnit, Math.abs(coefficient))} in {outcome}, on average.
				On its own, {focal} gets a slope of {fmt(simpleSlope)}, because it then also carries the {others} that go with it.
			</figcaption>
		</figure>
	);
}
